// @flow

import React from 'react';
import Slider from 'react-slick';
import classnames from 'classnames';

import styles from './styles.scss';
import type { AttributeType } from '../../api/types';
import studioImgPlaceholder from '../../assets/image/studioImgPlaceholder.png';
import { animatePanoramaCoverIn, animatePanoramaCoverOut, fadeInRight, animateMatrix } from '../../utils/animation';
import Panorama from './Panorama';
import CampusMap from './CampusMap';

type Props = {
  images: Array<AttributeType>,
  activeTabName: 'studioPhotos' | 'panorama' | 'campusMap' | 'studioFloorplan',
  index: number,
  studioInternalName: string,
  navigateToStudio: string => void,
};

const settings = {
  dots: false,
  arrows: false,
  infinite: true,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
};

const formatToURLOfImgType = (imgURL: string, targetFormat: string) => {
  const parts = imgURL.split('.');
  parts.splice(-1, 1, targetFormat);

  return parts.join('.');
};

class Image extends React.Component<Props> {
  componentDidMount() {
    if (this.props.activeTabName === 'panorama') {
      this.insertPanorama();
    }

    fadeInRight('[data-animation="fadeInRightTop"]').delay(0.8);
    fadeInRight('[data-animation="fadeInRightBottom"]').delay(1);
  }

  componentDidUpdate = (prevProps: Props) => {
    const isPanoramaView = this.props.activeTabName === 'panorama';
    const isRoomPhotosView = this.props.activeTabName === 'studioPhotos';
    const tabWasChanged = prevProps.activeTabName !== this.props.activeTabName;
    const panoramaImgChanged =
      (!prevProps.images.length && this.props.images.length) || prevProps.images[0].id !== this.props.images[0].id;

    if (isPanoramaView && panoramaImgChanged) {
      this.insertPanorama();
    }

    if (tabWasChanged && isRoomPhotosView) {
      animateMatrix();
      fadeInRight('[data-animation="fadeInRightTop"]').delay(0.8);
      fadeInRight('[data-animation="fadeInRightBottom"]').delay(1);
    }
  };

  insertPanorama = () => {
    const { value } = this.props.images[0];

    const panoLeftCover = document.querySelector('[data-active="true"] .left');
    const panoRightCover = document.querySelector('[data-active="true"] .right');

    const callback = async () => {
      await window.pannellum.viewer('panorama', JSON.parse(value)).destroy();
      await window.pannellum.viewer('panorama', JSON.parse(value));

      animatePanoramaCoverOut(panoLeftCover, panoRightCover).delay(1);
    };

    if (panoLeftCover && panoRightCover) {
      animatePanoramaCoverIn(panoLeftCover, panoRightCover, callback);
    }
  };

  renderImage = () => {
    const { activeTabName, studioInternalName, navigateToStudio } = this.props;

    if (activeTabName === 'panorama') {
      // $FlowFixMe
      document.body.classList.add('panorama');
      // $FlowFixMe
      document.body.classList.remove('roomPhotos');

      return this.props.images.length > 0 ? (
        <Panorama />
      ) : (
        <img src={studioImgPlaceholder} alt="Coming soon" className="active" />
      );
    }

    if (activeTabName === 'campusMap') {
      // $FlowFixMe
      document.body.classList.add('campusMap');
      // $FlowFixMe
      document.body.classList.remove('roomPhotos');
      // $FlowFixMe
      document.body.classList.remove('panorama');

      return <CampusMap studioInternalName={studioInternalName} navigateToStudio={navigateToStudio} />;
    }
    // $FlowFixMe
    document.body.classList.remove('campusMap');
    // $FlowFixMe
    document.body.classList.remove('panorama');
    // $FlowFixMe
    document.body.classList.add('roomPhotos');

    const sortedImages = this.props.images.sort((img1, img2) => img1.order - img2.order);

    return (
      <Slider key={this.props.studioInternalName} ref={c => (this.slider = c)} {...settings}>
        {sortedImages.map(el => (
          <picture key={el.value}>
            <source srcSet={formatToURLOfImgType(el.value, 'jp2')} type="image/jp2" />
            <source srcSet={formatToURLOfImgType(el.value, 'jxr')} type="image/vnd.ms-photo" />
            <source srcSet={formatToURLOfImgType(el.value, 'webp')} type="image/webp" />
            <img key={el.name} src={el.value} alt={el.name} />
          </picture>
        ))}
      </Slider>
    );
  };

  render() {
    const { images, index } = this.props;

    return (
      <React.Fragment>
        <div
          data-animation="matrix"
          data-index={index}
          // probably, "data-active" attr should be removed as there's only one rendered studio
          data-active
          className={classnames(styles['matrix-wrapper'], 'imageWrap')}
        >
          <div className="innerMask">{this.renderImage()}</div>
        </div>

        {images &&
          images.length > 1 && (
            <div id="sliderNav">
              <div className={styles.navigation}>
                <div
                  data-animation="fadeInRightTop"
                  data-clear
                  className={styles.prev}
                  onClick={() => this.slider.slickPrev()}
                >
                  <span />
                  prev
                </div>
                <div
                  data-animation="fadeInRightBottom"
                  data-clear
                  className={styles.next}
                  onClick={() => this.slider.slickNext()}
                >
                  next
                  <span />
                </div>
              </div>
            </div>
          )}
      </React.Fragment>
    );
  }
}

export default Image;



// WEBPACK FOOTER //
// ./src/components/Studio/Image.js